/* eslint-disable @typescript-eslint/no-explicit-any */
// Minimal, dependency-free Web Vitals capture (LCP, CLS, INP).
// Reports "bad" thresholds as warning-level events with source:"render".

import { addBreadcrumb } from "./breadcrumbs";

type VitalsReporter = (name: string, value: number, rating: "good" | "needs-improvement" | "poor") => void;

const THRESHOLDS = {
  LCP: { good: 2500, poor: 4000 },
  CLS: { good: 0.1, poor: 0.25 },
  INP: { good: 200, poor: 500 },
} as const;

function rate(name: keyof typeof THRESHOLDS, value: number): "good" | "needs-improvement" | "poor" {
  const t = THRESHOLDS[name];
  if (value <= t.good) return "good";
  if (value <= t.poor) return "needs-improvement";
  return "poor";
}

export function installVitalsCapture(report: VitalsReporter) {
  if (typeof window === "undefined" || typeof PerformanceObserver === "undefined") return;

  // LCP — largest-contentful-paint
  try {
    let lastLcp = 0;
    const po = new PerformanceObserver((list) => {
      for (const entry of list.getEntries() as PerformanceEntry[]) {
        lastLcp = (entry as any).renderTime || (entry as any).loadTime || entry.startTime;
      }
    });
    po.observe({ type: "largest-contentful-paint", buffered: true });

    const finalize = () => {
      if (lastLcp > 0) {
        const value = Math.round(lastLcp);
        addBreadcrumb("vitals", `LCP ${value}ms`, { value });
        report("LCP", value, rate("LCP", value));
      }
      po.disconnect();
    };
    addEventListener("pagehide", finalize, { once: true });
    document.addEventListener(
      "visibilitychange",
      () => document.visibilityState === "hidden" && finalize(),
      { once: true },
    );
  } catch {
    /* ignore */
  }

  // CLS — cumulative-layout-shift
  try {
    let cls = 0;
    const po = new PerformanceObserver((list) => {
      for (const entry of list.getEntries() as any[]) {
        if (!entry.hadRecentInput) cls += entry.value;
      }
    });
    po.observe({ type: "layout-shift", buffered: true });

    const finalize = () => {
      const value = Math.round(cls * 1000) / 1000;
      if (value > 0) {
        addBreadcrumb("vitals", `CLS ${value}`, { value });
        report("CLS", value, rate("CLS", value));
      }
      po.disconnect();
    };
    addEventListener("pagehide", finalize, { once: true });
    document.addEventListener(
      "visibilitychange",
      () => document.visibilityState === "hidden" && finalize(),
      { once: true },
    );
  } catch {
    /* ignore */
  }

  // INP — interaction-to-next-paint (via event timing)
  try {
    let worstInp = 0;
    const po = new PerformanceObserver((list) => {
      for (const entry of list.getEntries() as any[]) {
        if (entry.interactionId && entry.duration > worstInp) {
          worstInp = entry.duration;
        }
      }
    });
    po.observe({ type: "event", buffered: true, durationThreshold: 40 } as any);

    const finalize = () => {
      if (worstInp > 0) {
        const value = Math.round(worstInp);
        addBreadcrumb("vitals", `INP ${value}ms`, { value });
        report("INP", value, rate("INP", value));
      }
      po.disconnect();
    };
    addEventListener("pagehide", finalize, { once: true });
    document.addEventListener(
      "visibilitychange",
      () => document.visibilityState === "hidden" && finalize(),
      { once: true },
    );
  } catch {
    /* ignore */
  }
}

export function connectionInfo(): Record<string, unknown> {
  if (typeof navigator === "undefined") return {};
  const c = (navigator as any).connection;
  if (!c) return {};
  return {
    effectiveType: c.effectiveType,
    downlink: c.downlink,
    saveData: c.saveData,
    rtt: c.rtt,
  };
}
